import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

interface Book {
  id: number;
  title: string;
}

interface BookSelectorProps {
  value?: number;
  onChange: (bookId: number) => void;
  onlyWithEmbeddings?: boolean;
}

export function BookSelector({
  value,
  onChange,
  onlyWithEmbeddings = true,
}: BookSelectorProps) {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadBooks() {
      setLoading(true);

      if (onlyWithEmbeddings) {
        // Only books that have embeddings
        const { data } = await supabase
          .from("content_embeddings")
          .select("book_id, books(id, title)")
          .not("book_id", "is", null);

        if (data) {
          const uniqueBooks = new Map<number, Book>();
          for (const row of data) {
            const book = (row as any).books;
            if (book && !uniqueBooks.has(book.id)) {
              uniqueBooks.set(book.id, { id: book.id, title: book.title });
            }
          }
          setBooks(Array.from(uniqueBooks.values()).sort((a, b) => a.title.localeCompare(b.title)));
        }
      } else {
        const { data } = await supabase
          .from("books")
          .select("id, title")
          .eq("is_active", true)
          .order("title");

        setBooks(data || []);
      }

      setLoading(false);
    }

    loadBooks();
  }, [onlyWithEmbeddings]);

  return (
    <Select
      value={value?.toString()}
      onValueChange={(v) => onChange(Number(v))}
      disabled={loading}
    >
      <SelectTrigger className="w-[250px]">
        <SelectValue placeholder={loading ? "Loading books..." : "Select a book"} />
      </SelectTrigger>
      <SelectContent>
        {books.map((book) => (
          <SelectItem key={book.id} value={book.id.toString()}>
            {book.title}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
